import React, { useEffect, useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Button,
  Typography,
  Avatar,
  Dialog,
  Tabs,
  Tab,
  Chip,
  TextField,
  Divider,
  useTheme,
  Paper,
  MenuItem,
  ClickAwayListener,
  IconButton,
  Grid,
  alpha,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material"; // Import icons for quantity adjustment
import {
  createPOSOrder,
  getPOSCustomerLoyalty,
  processCloverPayment,
  processPOSPayment,
  searchPOSCustomers,
  processCloverRestPayment,
} from "../api/posApi";
import DiscountModal from "./DiscountModal";
import AddCustomerModal from "./AddCustomerModal";
import { OrderPayload } from "../types";
import { Order, sendReceipt } from "../api/orderApi";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import EmailIcon from "@mui/icons-material/Email";
import PrintIcon from "@mui/icons-material/Print";
import ConfirmDialog from "./ConfirmDialog";
import StarIcon from '@mui/icons-material/Star';
import { formatAttributes as fmtAttrs } from "../utils/variantAttributes";


interface ApiError {
  response?: {
    data?: {
      error?: string;
    };
  };
  message?: string;
}

interface POSOrderDetails {
  order_id: number;
  order_number: string;
  customer_name: string;
  customer_phone?: string;
  subtotal: number;
  discount_type: "percent" | "fixed" | "none";
  discount_value: number;
  loyalty_points_used: number;
  tax: number;
  total_price: number;
  created_at: string;
  payment_status: string;
  payment_method: string;
  loyalty_points_earned: number; // Changed from loyalty_points_awarded

  items: Array<{
    product_id: number;
    variant_id: number;
    product_name: string;
    variant_name: string;
    price: number;
    quantity: number;
    attributes?: { name: string; values: string[] }[];
    image_url?: string;
  }>;
}

interface OrderItem {
  variant_id: number;
  product_id: number;
  name: string;
  price: number;
  quantity: number;
  image_url?: string;
  attributes?: { name: string; values: string[] }[];
}

interface User {
  id: number;
  name: string;
  phone?: string;
  email?: string;
}

interface OrderListSellProps {
  orderItems: OrderItem[];
  onRemoveFromOrder: (variantId: number) => void;
  onClearOrder: () => void;
  onUpdateQuantity: (variantId: number, quantity: number) => void; // Add this prop
}

const OrderListSell: React.FC<OrderListSellProps> = ({
  orderItems,
  onRemoveFromOrder,
  onClearOrder,
  onUpdateQuantity, // Add this prop
}) => {
  const formatAttributes = (attributes?: any) =>
  fmtAttrs(attributes, { sep: " | ", empty: "No Options" });

  const theme = useTheme();
  const [localItems, setLocalItems] = useState<OrderItem[]>(() => {
    const saved = localStorage.getItem("posCart");
    return saved ? JSON.parse(saved) : orderItems;
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const searchRef = React.useRef<HTMLDivElement>(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [currentOrderDetails, setCurrentOrderDetails] = useState<POSOrderDetails | null>(null);

  // Payment and Discount States
  const [discount, setDiscount] = useState({ type: "percent", value: 0 });
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [cashReceived, setCashReceived] = useState(0);
  const [isProcessingCloverPayment, setIsProcessingCloverPayment] = useState(false);
  const [cardType, setCardType] = useState<"credit" | "debit">("credit");
  const [cashReceivedInput, setCashReceivedInput] = useState('0');

  // Loyalty Balance
  const [loyaltyPoints, setLoyaltyPoints] = useState<number>(0);
  const [pointsToRedeem, setPointsToRedeem] = useState<number>(0);

  // Calculations
  const subtotal = orderItems.reduce((total, item) => total + item.price * item.quantity, 0);
  const discountAmount = discount.type === "percent" ? subtotal * (discount.value / 100) : discount.value;
  const tax = (subtotal - discountAmount) * 0.0;
  const redeemedValue = Number(pointsToRedeem) || 0;
  const total = Math.max(subtotal - discountAmount + tax - redeemedValue, 0);
  const change = cashReceived - total;

  // confirm dialog 
  const [overrideStockDialogOpen, setOverrideStockDialogOpen] = useState(false);
  const [pendingRetryPayload, setPendingRetryPayload] = useState<any | null>(null);


  useEffect(() => {
    localStorage.setItem("posCart", JSON.stringify(localItems));
  }, [localItems]);

  useEffect(() => {
    const savedUser = localStorage.getItem("selectedPOSCustomer");
    if (savedUser) {
      setSelectedUser(JSON.parse(savedUser));
    }
  }, []);

  useEffect(() => {
    if (selectedUser) {
      const fetchLoyaltyPoints = async () => {
        try {
          const storeId = localStorage.getItem("store_id");
          if (!storeId) {
            console.warn("Store ID is missing!");
            return;
          }
          const data = await getPOSCustomerLoyalty(selectedUser.id, Number(storeId));
          setLoyaltyPoints(Number(data.balance) || 0);
        } catch (error) {
          console.error("Error fetching loyalty balance:", error);
        }
      };
      fetchLoyaltyPoints();
    }
  }, [selectedUser]);

  const handleRedeemLoyalty = () => {
    if (!selectedUser || pointsToRedeem <= 0 || isNaN(pointsToRedeem)) return;
    const maxRedeemable = Math.min(pointsToRedeem, loyaltyPoints, total);
    setPointsToRedeem(maxRedeemable);
  };

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers([]);
      setOpenDropdown(false);
      setNoResults(false);
      return;
    }

    const fetchCustomers = async () => {
      try {
        const results = await searchPOSCustomers(searchTerm);
        setFilteredUsers(results);
        setNoResults(results.length === 0);
        setOpenDropdown(true);
      } catch (error) {
        console.error("Error searching customers:", error);
      }
    };

    const debounceTimer = setTimeout(fetchCustomers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleSelectUser = (user: User) => {
    setSelectedUser(user);
    setSearchTerm("");
    setOpenDropdown(false);
    localStorage.setItem("selectedPOSCustomer", JSON.stringify(user));
  };

  // Handle quantity updates
  const handleQuantityChange = (variantId: number, newQuantity: number) => {
    if (newQuantity < 1) return; // Ensure quantity is at least 1
    onUpdateQuantity(variantId, newQuantity); // Call the parent function to update quantity
  };

  const generateOrderPDF = (order: POSOrderDetails) => {
    const doc = new jsPDF();
    const storeName = localStorage.getItem("store_name") || "Our Store";

    // Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(theme.palette.primary.main);
    doc.text(`${storeName}`, 105, 15, { align: "center" });
    doc.setFontSize(14);
    doc.text("IN-STORE RECEIPT", 105, 22, { align: "center" });

    // Add customer info and date
    doc.setFontSize(12);
    doc.setTextColor(90);
    doc.text(`Customer: ${order.customer_name}`, 15, 30);
    doc.text(`Date: ${new Date(order.created_at).toLocaleString()}`, 15, 36);

    // Items table
    autoTable(doc, {
      startY: 45,
      head: [["Product", "Variant", "Price", "Qty", "Total"]],
      body: order.items.map((item) => [
        item.product_name,
        item.variant_name,
        `$${item.price.toFixed(2)}`,
        item.quantity,
        `$${(item.quantity * item.price).toFixed(2)}`,
      ]),
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: theme.palette.primary.main },
    });

    // Totals
    const finalY = (doc as any).lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.text(`Subtotal: $${order.subtotal.toFixed(2)}`, 15, finalY);
    doc.text(`Discount: -$${(order.discount_type === "percent" ?
      (order.subtotal * order.discount_value / 100) :
      order.discount_value).toFixed(2)}`, 15, finalY + 6);
    doc.text(`Loyalty Points Used: -$${order.loyalty_points_used.toFixed(2)}`, 15, finalY + 12);
    doc.text(`Tax: $${order.tax.toFixed(2)}`, 15, finalY + 18);
    doc.setFontSize(14);
    doc.text(`Total: $${order.total_price.toFixed(2)}`, 15, finalY + 26);

    doc.save(`order-${order.order_number}.pdf`);
  };


  const handlePayment = async (cardType?: 'credit' | 'debit') => {
    const finalTotal = parseFloat((subtotal - discountAmount + tax).toFixed(2));
    const loyaltyApplied = selectedUser ? Math.min(pointsToRedeem, finalTotal) : 0;
    const amountToPay = Math.max(finalTotal - loyaltyApplied, 0);
    const storeId = localStorage.getItem("store_id") || "1";

    // ✅ Validate Order Items
    if (orderItems.length === 0) {
      alert("No items in the order.");
      return;
    }

    // ✅ Validate Loyalty Points
    if (selectedUser && loyaltyApplied > 0 && loyaltyApplied > loyaltyPoints) {
      alert("Insufficient loyalty points. Please adjust the amount to redeem.");
      return;
    }

    console.log("🛒 Selected Customer Before Payment:", selectedUser);

    try {
      const discountAmount = discount.type === "percent"
        ? subtotal * (discount.value / 100)
        : discount.value;

      const tax = (subtotal - discountAmount) * 0.00;
      let finalTotal = (subtotal - discountAmount) + tax;
      const roundedCashReceived = parseFloat((activeTab === 0 ? cashReceived : 0).toFixed(2));

      const loyaltyApplied = selectedUser ? Math.min(pointsToRedeem, finalTotal) : 0;
      finalTotal -= loyaltyApplied;
      finalTotal = parseFloat(finalTotal.toFixed(2));

      const orderPayload: OrderPayload = {
        customer_id: selectedUser ? selectedUser.id : undefined,
        customer_name: selectedUser ? selectedUser.name : "Walk-in",
        store_id: Number(storeId),
        items: orderItems.map((item) => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
        })),
        discount: {
          type: discount.type || "none",
          value: discount.value ?? 0,
        },
        payment: {
          method: activeTab === 0 ? "cash" : activeTab === 1 ? "card" : "loyalty",
          amount: finalTotal,
          cash_received: roundedCashReceived,
          store_credit_used: 0,
        },
      };

      console.log("🛒 Sending Order Payload:", orderPayload);

      const newOrder = await createPOSOrder(orderPayload);

      if (!newOrder.order_id) {
        throw new Error("Order creation failed.");
      }

      console.log("✅ Order created successfully:", newOrder.order_id);

      // Payment payload builder
      const buildPaymentPayload = (override = false) => ({
        order_id: newOrder.order_id,
        payment_method: activeTab === 2 ? "loyalty" : activeTab === 0 ? "cash" : "card",
        amount: amountToPay,
        cash_received: roundedCashReceived,
        store_credit_used: 0,
        loyalty_points_redeemed: loyaltyApplied ?? 0,
        store_id: Number(storeId),
        customer_id: selectedUser ? selectedUser.id : undefined,
        force_override_stock: override,
      });

      let paymentResponse;

      if (activeTab === 1) {
        if (!cardType) {
          alert("Please select a card type (Credit or Debit).");
          return;
        }

        console.log("💳 Processing Clover Card Payment...");
        setIsProcessingCloverPayment(true);

        paymentResponse = await processCloverPayment({
          order_id: newOrder.order_id,
          amount: finalTotal,
          store_id: Number(storeId),
          customer_id: selectedUser?.id,
          card_type: cardType,
        });

        setIsProcessingCloverPayment(false);
      } else {
        console.log("💵 Processing Non-Clover Payment...");

        try {
          paymentResponse = await processPOSPayment(buildPaymentPayload());
        } catch (error: any) {
          const errorMsg = error?.response?.data?.error;
          if (errorMsg?.includes("Insufficient stock")) {
            setPendingRetryPayload(buildPaymentPayload(true));
            setOverrideStockDialogOpen(true);
            return;
          } else {
            throw error;
          }
        }
      }

      if (!paymentResponse || paymentResponse.payment_status !== "paid") {
        throw new Error("Payment processing failed.");
      }

      console.log("✅ Payment processed successfully:", paymentResponse);

      // Get the actual awarded points from the backend response
      const loyaltyPointsAwarded = paymentResponse.loyalty_points_awarded || 0;

      // Update loyalty balance after payment
      if (selectedUser) {
        try {
          const updatedLoyalty = await getPOSCustomerLoyalty(selectedUser.id, Number(storeId));
          console.log("🏆 Updated Loyalty Balance:", updatedLoyalty);
          setLoyaltyPoints(updatedLoyalty.balance || 0);
        } catch (error) {
          console.error("❌ Failed to update loyalty balance:", error);
        }
      }

      const orderDetails: POSOrderDetails = {
        order_id: newOrder.order_id,
        order_number: newOrder.order_number || `POS-${new Date().getTime()}`,
        customer_name: selectedUser?.name || "Walk-in Customer",
        customer_phone: selectedUser?.phone,
        subtotal: subtotal,
        discount_type: discount.type as "percent" | "fixed" | "none",
        discount_value: discount.value,
        loyalty_points_used: pointsToRedeem,
        loyalty_points_earned: paymentResponse.loyalty_points_earned || 0, // Add the awarded points from backend
        tax: tax,
        total_price: total,
        created_at: new Date().toISOString(),
        payment_status: "paid",
        payment_method: activeTab === 0 ? "cash" : activeTab === 1 ? "card" : "loyalty",
        items: orderItems.map(item => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          product_name: item.name,
          variant_name: formatAttributes(item.attributes),
          price: item.price,
          quantity: item.quantity,
          attributes: item.attributes,
          image_url: item.image_url
        })),
      };

      // Reset all payment-related states
      onClearOrder();
      setPaymentDialogOpen(false);
      setSelectedUser(null);
      setPointsToRedeem(0);
      setCashReceivedInput('0');
      setCashReceived(0);
      localStorage.removeItem("selectedPOSCustomer");
      setCurrentOrderDetails(orderDetails);
      setSuccessDialogOpen(true);

    } catch (error) {
      console.error("🚨 Order or Payment Processing Error:", error);
      alert("❌ Failed to complete the order and payment.");
    }
  };


  const handleCloverFlexPayment = async () => {
    setIsProcessingCloverPayment(true);
    const storeId = Number(localStorage.getItem("store_id") || "1");

    try {
      const orderPayload: OrderPayload = {
        customer_id: selectedUser?.id,
        customer_name: selectedUser ? selectedUser.name : "Walk-in",
        store_id: storeId,
        items: orderItems.map(item => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
        })),
        discount: { type: discount.type, value: discount.value },
        payment: {
          method: "card",
          amount: total,
          cash_received: 0,
          store_credit_used: 0,
          loyalty_points_redeemed: 0,
        },
      };

      const newOrder = await createPOSOrder(orderPayload);
      if (!newOrder.order_id) throw new Error("Order creation failed.");

      const cloverPaymentResponse = await processCloverRestPayment(newOrder.order_id, total);

      if (cloverPaymentResponse.status === "paid") {
        alert("✅ Payment successful!");
        onClearOrder();
        setPaymentDialogOpen(false);
        setSelectedUser(null);
        localStorage.removeItem("selectedPOSCustomer");
        window.location.reload();
      } else {
        throw new Error("Clover payment failed. Check terminal.");
      }

    } catch (error: any) {
      alert("❌ Clover Flex payment error: " + error.message);
    } finally {
      setIsProcessingCloverPayment(false);
    }
  };
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 2, bgcolor: "background.paper", borderRadius: 2, color: "text.primary" }}>
      {/* Customer Search Section */}
      <Box sx={{ mb: 2, position: "relative" }} ref={searchRef}>
        <TextField
          label="Search customer by phone or name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          size="small"
          onFocus={() => searchTerm && setOpenDropdown(true)}
        />

        {openDropdown && filteredUsers.length > 0 && (
          <ClickAwayListener onClickAway={() => setOpenDropdown(false)}>
            <Paper sx={{
              position: "absolute",
              width: "100%",
              zIndex: 10,
              maxHeight: 200,
              overflowY: "auto",
            }}>
              {filteredUsers.map((user) => (
                <MenuItem key={user.id} onClick={() => handleSelectUser(user)}>
                  <Typography>
                    {user.name} {user.phone && `(${user.phone})`}
                  </Typography>
                </MenuItem>
              ))}
            </Paper>
          </ClickAwayListener>
        )}

        {noResults && (
          <Button
            variant="outlined"
            sx={{ mt: 1 }}
            onClick={() => setIsCreatingUser(true)}
          >
            + Add "{searchTerm}" as new customer
          </Button>
        )}
      </Box>

      {/* Selected Customer Display */}
      {selectedUser && (
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          p: 1.5,
          backgroundColor: theme.palette.mode === 'light'
            ? alpha(theme.palette.primary.light, 0.1)
            : alpha(theme.palette.primary.dark, 0.2),
          borderRadius: 2,
          border: `1px solid ${theme.palette.mode === 'light'
              ? theme.palette.primary.light
              : theme.palette.primary.dark
            }`,
          boxShadow: theme.shadows[1]
        }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {selectedUser.name}
            </Typography>
            {selectedUser.phone && (
              <Typography variant="body2" color="text.secondary">
                {selectedUser.phone}
              </Typography>
            )}
            <Typography
              variant="body2"
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                mt: 0.5
              }}
            >
              Loyalty Balance: ${(Number(loyaltyPoints) || 0).toFixed(2)}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSelectedUser(null)}
            sx={{
              borderRadius: 20,
              px: 2,
              textTransform: 'none',
              borderWidth: 1.5,
              '&:hover': {
                borderWidth: 1.5
              }
            }}
          >
            Change
          </Button>
        </Box>
      )}
      <AddCustomerModal
        open={isCreatingUser}
        onClose={() => setIsCreatingUser(false)}
        onCustomerCreated={(user) => {
          setSelectedUser(user);
          setIsCreatingUser(false);
          setSearchTerm("");
        }}
        searchTerm={searchTerm}
      />

      {/* Order List Section */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Current Sale</Typography>
        <Chip label={`${orderItems.length} items`} />
      </Box>

      <List sx={{ mb: 2, flex: 1, minHeight: 0, overflowY: "auto" }}>
        {orderItems.map((item) => (
          <ListItem key={item.variant_id} sx={{ px: 0 }}>
            <Avatar src={item.image_url} sx={{ width: 40, height: 40, mr: 2 }} />
            <ListItemText
              primary={`${formatAttributes(item.attributes)}`}
              secondary={`$${(item.price * item.quantity).toFixed(2)}`}
            />
            {/* Quantity Adjustment */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => handleQuantityChange(item.variant_id, item.quantity - 1)}
                disabled={item.quantity <= 1}
              >
                <Remove />
              </IconButton>
              <TextField
                value={item.quantity}
                onChange={(e) => {
                  const newQuantity = parseInt(e.target.value, 10);
                  if (!isNaN(newQuantity)) {
                    handleQuantityChange(item.variant_id, newQuantity);
                  }
                }}
                type="number"
                size="small"
                sx={{ width: 60 }}
                inputProps={{ min: 1 }}
              />
              <IconButton
                size="small"
                onClick={() => handleQuantityChange(item.variant_id, item.quantity + 1)}
              >
                <Add />
              </IconButton>
            </Box>
            <Button size="small" onClick={() => onRemoveFromOrder(item.variant_id)}>
              Remove
            </Button>
          </ListItem>
        ))}
      </List>

      {/* Totals Section */}
      <Box sx={{
        bgcolor: 'action.hover',
        p: 2,
        borderRadius: 1,
        mb: 2,
        backgroundColor: theme.palette.mode === "dark" ?
          theme.palette.grey[800] : theme.palette.grey[100]
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography>Subtotal:</Typography>
          <Typography>${subtotal.toFixed(2)}</Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography>Discount:</Typography>
            <Button size="small" onClick={() => setDiscountModalOpen(true)}>
              {discount.value ? `-$${discountAmount.toFixed(2)}` : "Add Discount"}
            </Button>
          </Box>
          <Typography color="error">-${discountAmount.toFixed(2)}</Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography>Tax (0%):</Typography>
          <Typography>${tax.toFixed(2)}</Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="subtitle1">Total:</Typography>
          <Typography variant="subtitle1">${total.toFixed(2)}</Typography>
        </Box>

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={() => setPaymentDialogOpen(true)}
          disabled={orderItems.length === 0}
        >
          Process Payment
        </Button>
      </Box>

      <DiscountModal
        open={discountModalOpen}
        onClose={() => setDiscountModalOpen(false)}
        onApplyDiscount={(type, value) => setDiscount({ type, value })}
        subtotal={subtotal}
      />

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <Box sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Total Amount: ${total.toFixed(2)}
            </Typography>

            {selectedUser && loyaltyPoints > 0 && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2">Loyalty Balance</Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    label="Amount to redeem ($)"
                    type="number"
                    size="small"
                    value={pointsToRedeem}
                    onChange={(e) => {
                      const value = Math.min(Number(e.target.value), Number(loyaltyPoints));
                      setPointsToRedeem(isNaN(value) ? 0 : value);
                    }}
                    inputProps={{ max: Number(loyaltyPoints), step: 0.01 }}
                    sx={{ width: 140 }}
                  />
                  <Typography variant="body2">
                    (Available: ${(Number(loyaltyPoints) || 0).toFixed(2)})
                  </Typography>
                </Box>
              </Box>
            )}

            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
              <Tab label="Cash" />
              <Tab label="Card" />
              <Tab label="Loyalty" />
            </Tabs>
          </Box>

          {activeTab === 0 && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Amount Due: ${total.toFixed(2)}</Typography>
                <TextField
                  fullWidth
                  label="Cash Received"
                  type="number"
                  value={cashReceivedInput}
                  onChange={(e) => {
                    // Allow empty value or numeric input
                    if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                      setCashReceivedInput(e.target.value);
                    }
                  }}
                  onFocus={(e) => {
                    if (cashReceivedInput === '0') {
                      setCashReceivedInput('');
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '') {
                      setCashReceivedInput('0');
                    }
                    // Convert to number for calculations
                    setCashReceived(Number(e.target.value || 0));
                  }}
                  InputProps={{ startAdornment: "$" }}
                  inputProps={{
                    inputMode: 'decimal',
                    pattern: '[0-9]*\\.?[0-9]*'
                  }}
                />
                {cashReceived > 0 && (
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    Change: ${Math.max(change, 0).toFixed(2)}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
  {[5, 10, 20, 50, 100].map((amount) => (
    <Button
      key={amount}
      variant="outlined"
      onClick={() => {
        const newAmount = cashReceived + amount;
        setCashReceived(newAmount);
        setCashReceivedInput(newAmount.toString());
      }}
    >
      ${amount}
    </Button>
  ))}
  <Button
    variant="outlined"
    onClick={() => {
      setCashReceived(0);
      setCashReceivedInput('0');
    }}
  >
    Clear
  </Button>
</Box>
            </>
          )}

          {activeTab === 1 && (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <Typography variant="h6" gutterBottom>
                Card Payment via Clover Flex
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                ${total.toFixed(2)} will be charged to the card.
              </Typography>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleCloverFlexPayment}
                disabled={isProcessingCloverPayment}
              >
                {isProcessingCloverPayment ? "Processing Payment..." : "Pay with Card"}
              </Button>

              {isProcessingCloverPayment && (
                <Typography variant="body2" color="primary" sx={{ mt: 2 }}>
                  Please follow the prompts on the Clover Flex device...
                </Typography>
              )}
              {/* Success Dialog */}

            </Box>
          )}






          {activeTab === 2 && (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <Typography variant="h6" gutterBottom>
                Loyalty Payment
              </Typography>
              <Typography sx={{ mb: 2 }}>
                Available Balance: ${(Number(loyaltyPoints) || 0).toFixed(2)}
              </Typography>
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => {
                  setPointsToRedeem(loyaltyPoints);
                  handleRedeemLoyalty();
                }}
                disabled={loyaltyPoints === 0}
              >
                Use Full Balance
              </Button>
            </Box>
          )}

          {activeTab !== 1 && (
            <Button
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3 }}
              onClick={() => {
                if (activeTab === 0) {
                  handlePayment(); // Cash payment
                } else if (activeTab === 2) {
                  handlePayment(); // Loyalty payment
                }
              }}
              disabled={
                (activeTab === 0 && (cashReceived < total || cashReceived <= 0)) ||
                (activeTab === 2 && loyaltyPoints < total)
              }
            >
              {activeTab === 0 ? "Complete Cash Payment" : "Redeem Loyalty Balance"}
            </Button>
          )}

        </Box>
      </Dialog>
      <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)} maxWidth="md" fullWidth>
  <Box sx={{ p: 3 }}>
    <Typography variant="h4" gutterBottom sx={{ color: theme.palette.success.main }}>
      Order Complete! 🎉
    </Typography>

    {currentOrderDetails && (
      <>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Typography variant="subtitle1">
              Order #: {currentOrderDetails.order_number}
            </Typography>
            <Typography variant="body2">
              Date: {new Date(currentOrderDetails.created_at).toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={6} textAlign="right">
            <Typography variant="h6">
              Total: ${currentOrderDetails.total_price.toFixed(2)}
            </Typography>
            <Chip
              label={currentOrderDetails.payment_status.toUpperCase()}
              color={currentOrderDetails.payment_status === "paid" ? "success" : "error"}
              size="small"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <List dense sx={{ mb: 3 }}>
          {currentOrderDetails.items.map((item, index) => (
            <ListItem key={index} sx={{ px: 0 }}>
              <Avatar src={item.image_url} sx={{ width: 40, height: 40, mr: 2 }} />
              <ListItemText
                primary={item.product_name}
                secondary={item.variant_name}
              />
              <Typography variant="body2">
                {item.quantity} × ${item.price.toFixed(2)}
              </Typography>
            </ListItem>
          ))}
        </List>

        <Box sx={{
          backgroundColor: theme.palette.grey[100],
          p: 2,
          borderRadius: 1,
          mb: 3
        }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>Subtotal:</Grid>
            <Grid item xs={6} textAlign="right">
              ${currentOrderDetails.subtotal.toFixed(2)}
            </Grid>

            <Grid item xs={6}>Discount:</Grid>
            <Grid item xs={6} textAlign="right" color="error">
              -${(currentOrderDetails.discount_type === "percent" ?
                (currentOrderDetails.subtotal * currentOrderDetails.discount_value / 100) :
                currentOrderDetails.discount_value).toFixed(2)}
            </Grid>

            <Grid item xs={6}>Loyalty Points Used:</Grid>
            <Grid item xs={6} textAlign="right" color="primary">
              -${currentOrderDetails.loyalty_points_used.toFixed(2)}
            </Grid>

            <Grid item xs={6}>Tax:</Grid>
            <Grid item xs={6} textAlign="right">
              ${currentOrderDetails.tax.toFixed(2)}
            </Grid>

            {/* Added Loyalty Points Earned Section */}
            {currentOrderDetails?.loyalty_points_earned > 0 && (
  <>
    <Grid item xs={6}>
      <Typography sx={{ fontWeight: 500 }}>
        Rewards Points Earned:
      </Typography>
    </Grid>
    <Grid item xs={6} textAlign="right">
      <Typography sx={{ 
        fontWeight: 600, 
        color: theme.palette.success.main,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5
      }}>
        <StarIcon fontSize="small" color="success" />
        +${currentOrderDetails.loyalty_points_earned.toFixed(2)}
      </Typography>
    </Grid>
  </>
)}
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => generateOrderPDF(currentOrderDetails)}
          >
            Print
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (currentOrderDetails) {
                sendReceipt(currentOrderDetails.order_id)
                  .then(() => alert("Receipt sent successfully!"))
                  .catch(() => alert("Error sending receipt"));
              }
            }}
            startIcon={<EmailIcon />}
          >
            Email Receipt
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setSuccessDialogOpen(false);
              setCashReceivedInput('0');
              setCashReceived(0);
            }}
          >
            Next Sale
          </Button>
        </Box>
      </>
    )}
  </Box>
</Dialog>
      <ConfirmDialog
  open={overrideStockDialogOpen}
  title="Confirm Stock Override"
  message="⚠️ Some items are out of stock. Continuing will allow inventory to go negative. Do you want to proceed?"
  onClose={() => {
    setOverrideStockDialogOpen(false);
    setPendingRetryPayload(null);
  }}
  onConfirm={async () => {
    setOverrideStockDialogOpen(false);

    try {
      const response = await processPOSPayment(pendingRetryPayload);

      if (response.payment_status === "paid") {
        // Get the actual awarded points from the backend response
        const loyaltyPointsAwarded = response.loyalty_points_awarded || 0;

        const orderDetails: POSOrderDetails = {
          order_id: pendingRetryPayload.order_id,
          order_number: `#${pendingRetryPayload.order_id}`,
          customer_name: selectedUser?.name || "Walk-in",
          customer_phone: selectedUser?.phone,
          subtotal,
          discount_type: discount.type as "percent" | "fixed" | "none",
          discount_value: discount.value,
          loyalty_points_used: pointsToRedeem,
          loyalty_points_earned: loyaltyPointsAwarded, // Added this line
          tax,
          total_price: total,
          created_at: new Date().toISOString(),
          payment_status: "paid",
          payment_method: pendingRetryPayload.payment_method,
          items: orderItems.map(item => ({
            product_id: item.product_id,
            variant_id: item.variant_id,
            product_name: item.name,
            price: item.price,
            quantity: item.quantity,
            attributes: item.attributes || [],
            variant_name: formatAttributes(item.attributes),
            image_url: item.image_url,
          })),
        };

        // Reset all payment-related states
        onClearOrder();
        setPaymentDialogOpen(false);
        setSelectedUser(null);
        setPointsToRedeem(0);
        setCashReceivedInput('0');
        setCashReceived(0);
        localStorage.removeItem("selectedPOSCustomer");
        setCurrentOrderDetails(orderDetails);
        setSuccessDialogOpen(true);
      }
    } catch (err: unknown) {
      console.error("❌ Retry with stock override failed:", err);
      const error = err as ApiError;
      alert(
        error?.response?.data?.error ||
        error?.message ||
        "Still failed. Please check stock or contact support."
      );
    }
  }}
/>

    </Box>
  );
};

export default OrderListSell;